import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { wpOffersAPI, WP_PRODUCT_GROUPS } from "../lib/api";
import { toast } from "sonner";
import {
  ArrowLeft, Printer, Send, CheckCircle, Loader2, Flame, Download, Mail, Tag, Copy,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import IncognitoBadge from "../components/IncognitoBadge";
import IncognitoToggleButton from "../components/IncognitoToggleButton";
import { useAuth } from "../context/AuthContext";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import WPProfitabilityCard from "../components/WPProfitabilityCard";

const STATUS_BADGES = {
  erstellt: { label: "Erstellt", cls: "" },
  versendet: { label: "Versendet", cls: "bg-blue-600" },
  angenommen: { label: "Angenommen", cls: "bg-green-600" },
  abgelehnt: { label: "Abgelehnt", cls: "bg-red-600" },
};

export default function WPOfferDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [offer, setOffer] = useState(null);
  const [sending, setSending] = useState(false);
  const [accepting, setAccepting] = useState(false);
  const [duplicating, setDuplicating] = useState(false);
  const [reassigningIdx, setReassigningIdx] = useState(null);

  useEffect(() => {
    load();
  }, [id]);

  const load = async () => {
    setLoading(true);
    try {
      const res = await wpOffersAPI.get(id);
      setOffer(res.data);
    } catch (err) {
      toast.error("Fehler beim Laden des WP-Angebots");
    } finally {
      setLoading(false);
    }
  };

  const handleReassignGroup = async (originalIndex, newGroup) => {
    setReassigningIdx(originalIndex);
    try {
      await wpOffersAPI.updateItemGroup(id, originalIndex, newGroup);
      toast.success("Produktgruppe aktualisiert");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Aktualisierung fehlgeschlagen");
    } finally {
      setReassigningIdx(null);
    }
  };

  const handlePrint = () => {
    const url = wpOffersAPI.getPdfUrl(id);
    window.open(url, "_blank");
  };

  const handleSend = async () => {
    setSending(true);
    try {
      const res = await wpOffersAPI.sendEmail(id);
      toast.success(res.data?.message || "Versand gestartet");
      // Optimistic update – background task may take a few seconds
      setTimeout(load, 4000);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Fehler beim Versand";
      toast.error(typeof msg === "string" ? msg : "Fehler beim Versand");
    } finally {
      setSending(false);
    }
  };

  const handleMarkAccepted = async () => {
    setAccepting(true);
    try {
      await wpOffersAPI.markAccepted(id);
      toast.success("Angebot als angenommen markiert");
      load();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Fehler beim Markieren";
      toast.error(typeof msg === "string" ? msg : "Fehler");
    } finally {
      setAccepting(false);
    }
  };

  const handleDuplicate = async () => {
    setDuplicating(true);
    try {
      const res = await wpOffersAPI.duplicate(id);
      const newId = res.data?.id;
      const newNumber = res.data?.offer_number;
      toast.success(`Angebot dupliziert als ${newNumber}`);
      if (newId) {
        // Direkt zur neuen Detailseite weiterleiten
        navigate(`/wp-angebote/${newId}`);
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || "Duplizieren fehlgeschlagen";
      toast.error(typeof msg === "string" ? msg : "Fehler");
    } finally {
      setDuplicating(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin" data-testid="wp-offer-detail-loading" />
      </div>
    );
  }
  if (!offer) {
    return <div className="text-center py-12">WP-Angebot nicht gefunden</div>;
  }

  // Items pro Produktgruppe gruppieren (für die Anzeige) — original index mitschleifen
  // damit die manuelle Gruppen-Korrektur die richtige Position im Backend trifft.
  const itemsWithIdx = (offer.items || []).map((it, originalIndex) => ({ ...it, _originalIndex: originalIndex }));
  const grouped = WP_PRODUCT_GROUPS.reduce((acc, g) => {
    acc[g.key] = itemsWithIdx.filter((it) => (it.product_group || "sonstiges") === g.key);
    return acc;
  }, {});

  const badge = STATUS_BADGES[offer.status] || { label: offer.status, cls: "" };

  return (
    <div className="space-y-6" data-testid="wp-offer-detail-page">
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
        <div className="flex items-start gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate("/wp-angebote")} data-testid="back-to-list">
            <ArrowLeft className="h-4 w-4 mr-2" /> Zurück
          </Button>
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-2">
              <Flame className="h-7 w-7 text-orange-500" />
              {offer.offer_number}
              <IncognitoBadge active={offer.is_incognito} size="md" />
            </h1>
            <p className="text-sm text-muted-foreground">{offer.title}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <IncognitoToggleButton user={user} docType="wp_offer" docId={offer.id} active={offer.is_incognito} onToggled={load} />
          <Badge className={badge.cls} data-testid="status-badge">
            {badge.label}
          </Badge>
          <Button variant="outline" onClick={handlePrint} data-testid="print-pdf-btn">
            <Printer className="h-4 w-4 mr-2" /> PDF
          </Button>
          <Button
            variant="outline"
            onClick={handleDuplicate}
            disabled={duplicating}
            data-testid="duplicate-offer-btn"
          >
            {duplicating ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Copy className="h-4 w-4 mr-2" />}
            Angebot duplizieren
          </Button>
          {offer.status === "erstellt" && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button data-testid="send-btn" disabled={sending}>
                  {sending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
                  An Kunden senden
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>WP-Angebot per E-Mail senden?</AlertDialogTitle>
                  <AlertDialogDescription>
                    Das Angebot wird im Hintergrund an die hinterlegte E-Mail-Adresse des Kunden versendet.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel data-testid="send-cancel">Abbrechen</AlertDialogCancel>
                  <AlertDialogAction onClick={handleSend} data-testid="send-confirm">
                    Jetzt senden
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
          {(offer.status === "erstellt" || offer.status === "versendet") && (
            <Button variant="outline" onClick={handleMarkAccepted} disabled={accepting} data-testid="mark-accepted-btn">
              {accepting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle className="h-4 w-4 mr-2" />}
              Als angenommen markieren
            </Button>
          )}
        </div>
      </div>

      {offer.send_error && (
        <Card className="border-red-300 bg-red-50">
          <CardContent className="py-3 text-sm text-red-700">
            <Mail className="h-4 w-4 inline mr-2" />
            E-Mail-Versand fehlgeschlagen: {offer.send_error}
          </CardContent>
        </Card>
      )}

      <div className="grid md:grid-cols-2 gap-4">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Kunde</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="font-semibold">{offer.customer_name}</div>
            <pre className="whitespace-pre-wrap text-sm text-muted-foreground font-sans">{offer.customer_address}</pre>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Wärme-Kennzahlen</CardTitle>
          </CardHeader>
          <CardContent className="text-sm space-y-1">
            {offer.heat_demand_kwh != null && <div>Wärmebedarf: <strong>{offer.heat_demand_kwh} kWh/Jahr</strong></div>}
            {offer.current_heating_type && <div>Aktuelles Heizsystem: <strong>{offer.current_heating_type}</strong></div>}
            {offer.current_energy_cost_eur != null && <div>Heizkosten/Jahr: <strong>{offer.current_energy_cost_eur} €</strong></div>}
            {offer.expected_savings_eur != null && <div className="text-green-700">Erwartete Einsparung: <strong>{offer.expected_savings_eur} €/Jahr</strong></div>}
            {offer.heat_demand_kwh == null && !offer.current_heating_type && !offer.current_energy_cost_eur && !offer.expected_savings_eur && (
              <div className="text-muted-foreground italic">Keine Wärme-Kennzahlen erfasst.</div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Positionen nach Gruppen */}
      {(offer.items?.length > 0) && (
        <div className="text-xs text-muted-foreground flex items-center gap-2" data-testid="group-edit-hint">
          <Tag className="h-3.5 w-3.5" />
          <span>Tipp: Über das Tag-Icon in jeder Zeile lässt sich die Produktgruppe einer Position nachträglich korrigieren — z. B. wenn ein Import sie in „Sonstiges" abgelegt hat.</span>
        </div>
      )}
      {WP_PRODUCT_GROUPS.map((group) => {
        const items = grouped[group.key] || [];
        if (items.length === 0) return null;
        const groupTotal = items.reduce((sum, it) => sum + (it.total || 0), 0);
        return (
          <Card key={group.key} data-testid={`group-${group.key}`}>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center justify-between">
                <span>{group.label}</span>
                <span className="text-sm font-normal text-muted-foreground">
                  Summe: <strong className="text-foreground">{groupTotal.toFixed(2)} €</strong>
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Beschreibung</TableHead>
                    <TableHead className="text-right">Menge</TableHead>
                    <TableHead>Einheit</TableHead>
                    <TableHead className="text-right">Einzelpreis</TableHead>
                    <TableHead className="text-right">Gesamt</TableHead>
                    <TableHead className="w-[180px]">Gruppe</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map((it) => (
                    <TableRow key={it._originalIndex} data-testid={`wp-item-row-${it._originalIndex}`}>
                      <TableCell>{it.description}</TableCell>
                      <TableCell className="text-right">{it.quantity}</TableCell>
                      <TableCell>{it.unit}</TableCell>
                      <TableCell className="text-right">{(it.unit_price || 0).toFixed(2)} €</TableCell>
                      <TableCell className="text-right font-semibold">{(it.total || 0).toFixed(2)} €</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          {reassigningIdx === it._originalIndex ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
                          ) : (
                            <Tag className="h-3.5 w-3.5 text-muted-foreground" />
                          )}
                          <Select
                            value={it.product_group || "sonstiges"}
                            onValueChange={(v) => handleReassignGroup(it._originalIndex, v)}
                            disabled={reassigningIdx !== null}
                          >
                            <SelectTrigger
                              className="h-7 text-xs border-dashed"
                              data-testid={`wp-item-group-select-${it._originalIndex}`}
                            >
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {WP_PRODUCT_GROUPS.map((g) => (
                                <SelectItem key={g.key} value={g.key} data-testid={`wp-group-option-${g.key}`}>
                                  {g.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        );
      })}

      {/* Wirtschaftlichkeit */}
      <WPProfitabilityCard offer={offer} onUpdated={load} />

      {/* Summary */}
      <Card className="border-2 border-orange-300 bg-orange-50">
        <CardContent className="pt-6">
          <div className="space-y-1 text-right">
            <div className="text-sm">Netto: <span className="font-semibold">{(offer.subtotal || 0).toFixed(2)} €</span></div>
            {offer.include_vat && (
              <div className="text-sm">MwSt. ({offer.vat_rate}%): <span className="font-semibold">{(offer.vat_amount || 0).toFixed(2)} €</span></div>
            )}
            <div className="text-2xl font-bold" data-testid="total-display">
              Gesamt: {(offer.total || 0).toFixed(2)} €
            </div>
            <div className="text-xs text-muted-foreground">Zahlungsziel: {offer.payment_days} Tage</div>
          </div>
        </CardContent>
      </Card>

      {offer.notes && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Notizen</CardTitle>
          </CardHeader>
          <CardContent className="text-sm text-muted-foreground whitespace-pre-wrap">
            {offer.notes}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
